import React, { useEffect, useRef } from 'react';
import useEnergyConsultationForm from '../../hooks/useEnergyConsultationForm';
import BusinessInformationSection from './NewConsultationForm/BusinessInformationSection';
import AccountHolderInformationSection from './NewConsultationForm/AccountHolderInformationSection';
import ConsultationTypeSection from './NewConsultationForm/ConsultationTypeSection';
import BusinessEnergyNeedsSection from './NewConsultationForm/BusinessEnergyNeedsSection';
import AdditionalCommentsSection from './NewConsultationForm/AdditionalCommentsSection';
import { Box, Grid } from '@material-ui/core';
import PGEButton from '../buttons';
import { useTranslation } from '../../hooks/useTranslation';
import theme from '../../themes/theme';
import routes from '../../routes';
import { ConsultationType } from '../../__generated__/pge-types';
import { Alert } from '@material-ui/lab';

const PublicEnergyConsultationForm = () => {
  const { t } = useTranslation();
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const lastHeightRef = React.useRef<number>(0);
  const {
    form,
    formKey,
    availableSiteVisitDays,
    isFormSubmitting,
    publicFormErrorMessage,
    onSubmitPublicForm,
  } = useEnergyConsultationForm({
    isPublicForm: true,
    onSuccess: () =>
      (window.parent.location.href =
        routes.PUBLIC_ENERGY_CONSULTATION_FORM_CONFIRMATION),
  });
  const formRef = useRef<HTMLFormElement | null>(null);

  const onFormError = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    let timeout: number | null = null;

    const observer = new ResizeObserver(() => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
      timeout = window.setTimeout(() => {
        const height = contentRef.current?.offsetHeight || 0;

        // This will be displayed as an iframe in the Jot.tsx component, which adds 15px to the received height; subtract 15px here to prevent overflow.
        const finalHeight = Math.max(0, height - 15);

        if (window.parent !== window && finalHeight) {
          if (Math.abs(finalHeight - lastHeightRef.current) > 5) {
            lastHeightRef.current = finalHeight;
            window.parent.postMessage(`setHeight:${finalHeight}`, '*');
          }
        }
      }, 100); // debounce to prevent loops
    });

    observer.observe(contentRef.current);

    return () => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
      observer.disconnect();
    };
  }, []);

  const isSubmitDisabled =
    form.values.consultationType === ConsultationType.SiteVisit ||
    isFormSubmitting;

  return (
    <div ref={contentRef} style={{ width: '100%', padding: theme.spacing(5) }}>
      <form
        onSubmit={form.submit(onSubmitPublicForm, e => onFormError())}
        key={formKey}
        ref={formRef}
      >
        <BusinessInformationSection form={form} isPublicForm />

        <AccountHolderInformationSection form={form} isPublicForm />

        <ConsultationTypeSection
          form={form}
          availableSiteVisitDays={availableSiteVisitDays}
          isPublicForm
        />

        <BusinessEnergyNeedsSection form={form} />

        <AdditionalCommentsSection form={form} />

        {publicFormErrorMessage && (
          <Box css={{ margin: '8px 0' }}>
            <Alert severity="error">{publicFormErrorMessage}</Alert>
          </Box>
        )}

        <Grid container justify="flex-end">
          <Grid item css={{ display: 'flex', gap: '12px' }}>
            <PGEButton type="submit" disabled={isSubmitDisabled}>
              {isFormSubmitting ? t('SUBMITTING') : t('SUBMIT')}
            </PGEButton>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default PublicEnergyConsultationForm;
